import type { MessageDescriptor } from 'react-intl';

import {
  complimentaryEndedCopyMessages as copy,
  complimentaryEndedFeatureMessages as features,
  complimentaryEndedPlanMessages as plans,
} from './complimentary-ended-variants.messages';

/** The plan a member is on when their complimentary usage ends. */
export type ComplimentaryEndedPlan = 'free' | 'scribe_plus' | 'standard';

/** The seat the modal sells. */
export type ComplimentaryEndedTarget = 'clinician' | 'max';

export type ComplimentaryEndedVariantId =
  | 'free_light'
  | 'free_clinician'
  | 'free_max'
  | 'scribe_plus_light'
  | 'scribe_plus_clinician'
  | 'scribe_plus_max'
  | 'standard_light'
  | 'standard_near_limit'
  | 'standard_exceeded';

export type ComplimentaryEndedFeature = {
  label: MessageDescriptor;
  tip: MessageDescriptor;
};

export type ComplimentaryEndedFeatureList = {
  heading: MessageDescriptor;
  items: readonly ComplimentaryEndedFeature[];
};

export type ComplimentaryEndedVariant = {
  id: ComplimentaryEndedVariantId;
  currentPlan: ComplimentaryEndedPlan;
  target: ComplimentaryEndedTarget;
  /** Recommended variants preselect the target tab and lead with the upgrade. */
  isRecommended: boolean;
  title: MessageDescriptor;
  body: MessageDescriptor;
  upgradeLabel: MessageDescriptor;
  targetFeatures: ComplimentaryEndedFeatureList;
  currentFeatures: ComplimentaryEndedFeatureList;
};

const feature = (label: MessageDescriptor, tip: MessageDescriptor) => ({ label, tip });

const TEMPLATES = feature(features.unlimitedTemplates, features.advancedTemplatesTip);
const EVIDENCE_IN_PATIENT_VISITS = feature(
  features.evidenceInPatientVisits,
  features.evidenceInPatientVisitsTip
);
const DAILY_AGENTIC = feature(
  features.dailyAgentsAndRoutines,
  features.dailyAgenticUseTip
);
const MORE_AGENT_USAGE = feature(features.moreAgentUsage, features.moreAgentUsageTip);
const LIMITED_AGENT_USE = feature(features.limitedAgentUse, features.handHeidiATaskTip);
const PREMIUM_EVIDENCE_SOURCES = feature(
  features.premiumEvidenceSources,
  features.premiumEvidenceSourcesTip
);

const FREE_FEATURES: ComplimentaryEndedFeatureList = {
  heading: features.freeForever,
  items: [
    feature(features.unlimitedScribeNotes, features.unlimitedScribeNotesTip),
    feature(features.limitedAdvancedTemplates, features.advancedTemplatesTip),
    LIMITED_AGENT_USE,
  ],
};
const CLINICIAN_FROM_FREE: ComplimentaryEndedFeatureList = {
  heading: features.everythingInFree,
  items: [MORE_AGENT_USAGE, TEMPLATES, EVIDENCE_IN_PATIENT_VISITS],
};
const MAX_FROM_FREE: ComplimentaryEndedFeatureList = {
  heading: features.everythingInFree,
  items: [DAILY_AGENTIC, TEMPLATES, EVIDENCE_IN_PATIENT_VISITS],
};

const SCRIBE_PLUS_FEATURES: ComplimentaryEndedFeatureList = {
  heading: features.everythingInFree,
  items: [
    LIMITED_AGENT_USE,
    TEMPLATES,
    feature(features.limitedInSessionEvidence, features.limitedInSessionEvidenceTip),
  ],
};
const CLINICIAN_FROM_SCRIBE_PLUS: ComplimentaryEndedFeatureList = {
  heading: features.everythingInScribePlus,
  items: [MORE_AGENT_USAGE, PREMIUM_EVIDENCE_SOURCES, EVIDENCE_IN_PATIENT_VISITS],
};
const MAX_FROM_SCRIBE_PLUS: ComplimentaryEndedFeatureList = {
  heading: features.everythingInScribePlus,
  items: [DAILY_AGENTIC, PREMIUM_EVIDENCE_SOURCES, EVIDENCE_IN_PATIENT_VISITS],
};

const STANDARD_FEATURES: ComplimentaryEndedFeatureList = {
  heading: features.everythingInFree,
  items: [LIMITED_AGENT_USE, TEMPLATES, EVIDENCE_IN_PATIENT_VISITS],
};
const MAX_FROM_STANDARD: ComplimentaryEndedFeatureList = {
  heading: features.everythingInClinicianStandard,
  items: [DAILY_AGENTIC, feature(features.earlyAccess, features.earlyAccessTip)],
};

const free = {
  currentPlan: 'free',
  currentFeatures: FREE_FEATURES,
} as const;
const scribePlus = {
  currentPlan: 'scribe_plus',
  currentFeatures: SCRIBE_PLUS_FEATURES,
} as const;
const standard = {
  currentPlan: 'standard',
  currentFeatures: STANDARD_FEATURES,
  target: 'max',
  targetFeatures: MAX_FROM_STANDARD,
} as const;

export const COMPLIMENTARY_ENDED_VARIANTS: Record<
  ComplimentaryEndedVariantId,
  ComplimentaryEndedVariant
> = {
  free_light: {
    ...free,
    id: 'free_light',
    target: 'clinician',
    isRecommended: false,
    title: copy.titleEnded,
    body: copy.bodyLight,
    upgradeLabel: copy.upgradeToClinician,
    targetFeatures: CLINICIAN_FROM_FREE,
  },
  free_clinician: {
    ...free,
    id: 'free_clinician',
    target: 'clinician',
    isRecommended: true,
    title: copy.titleEnded,
    body: copy.bodyNearLimit,
    upgradeLabel: copy.upgradeToClinician,
    targetFeatures: CLINICIAN_FROM_FREE,
  },
  free_max: {
    ...free,
    id: 'free_max',
    target: 'max',
    isRecommended: true,
    title: copy.titleExceeded,
    body: copy.bodyBestMax,
    upgradeLabel: copy.upgradeToClinicianMax,
    targetFeatures: MAX_FROM_FREE,
  },
  scribe_plus_light: {
    ...scribePlus,
    id: 'scribe_plus_light',
    target: 'clinician',
    isRecommended: false,
    title: copy.titleEnded,
    body: copy.bodyLight,
    upgradeLabel: copy.upgradeToClinician,
    targetFeatures: CLINICIAN_FROM_SCRIBE_PLUS,
  },
  scribe_plus_clinician: {
    ...scribePlus,
    id: 'scribe_plus_clinician',
    target: 'clinician',
    isRecommended: true,
    title: copy.titleEnded,
    body: copy.bodyNearLimit,
    upgradeLabel: copy.upgradeToClinician,
    targetFeatures: CLINICIAN_FROM_SCRIBE_PLUS,
  },
  scribe_plus_max: {
    ...scribePlus,
    id: 'scribe_plus_max',
    target: 'max',
    isRecommended: true,
    title: copy.titleExceeded,
    body: copy.bodyBestMax,
    upgradeLabel: copy.upgradeToClinicianMax,
    targetFeatures: MAX_FROM_SCRIBE_PLUS,
  },
  standard_light: {
    ...standard,
    id: 'standard_light',
    isRecommended: false,
    title: copy.titleEnded,
    body: copy.bodyLight,
    upgradeLabel: copy.upgradeToClinicianMax,
  },
  standard_near_limit: {
    ...standard,
    id: 'standard_near_limit',
    isRecommended: true,
    title: copy.titleEnded,
    body: copy.bodyNearLimit,
    upgradeLabel: copy.upgradeToClinicianMax,
  },
  standard_exceeded: {
    ...standard,
    id: 'standard_exceeded',
    isRecommended: true,
    title: copy.titleExceeded,
    body: copy.bodyBestMax,
    upgradeLabel: copy.upgradeToClinicianMax,
  },
};

export const COMPLIMENTARY_ENDED_PLAN_NAMES: Record<
  ComplimentaryEndedPlan | ComplimentaryEndedTarget,
  MessageDescriptor
> = {
  free: plans.free,
  scribe_plus: plans.scribePlus,
  // Standard members see their seat named Clinician.
  standard: plans.clinician,
  clinician: plans.clinician,
  max: plans.max,
};

const toCredits = (value: string | null | undefined): number | null => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Bands usage against the plan limit: under 75% leads with staying. Past that,
 * Free and Scribe Plus are sold the suggested seat; Standard is near its limit
 * up to 100% and exceeded past it.
 */
export function resolveComplimentaryEndedVariant({
  currentPlan,
  consumed,
  currentLimit,
  suggestedTarget,
}: {
  currentPlan: ComplimentaryEndedPlan;
  consumed: string | null | undefined;
  currentLimit: string | null | undefined;
  /** The seat of the suggested Clinician plan, Clinician when there is none. */
  suggestedTarget: ComplimentaryEndedTarget;
}): ComplimentaryEndedVariant {
  const used = toCredits(consumed) ?? 0;
  const limit = toCredits(currentLimit);
  const ratio = limit != null && limit > 0 ? used / limit : 0;

  if (currentPlan === 'standard') {
    if (ratio < 0.75) return COMPLIMENTARY_ENDED_VARIANTS.standard_light;
    if (ratio <= 1) return COMPLIMENTARY_ENDED_VARIANTS.standard_near_limit;
    return COMPLIMENTARY_ENDED_VARIANTS.standard_exceeded;
  }

  const prefix = currentPlan === 'free' ? 'free' : 'scribe_plus';
  if (ratio < 0.75) return COMPLIMENTARY_ENDED_VARIANTS[`${prefix}_light`];
  if (suggestedTarget === 'max') return COMPLIMENTARY_ENDED_VARIANTS[`${prefix}_max`];
  return COMPLIMENTARY_ENDED_VARIANTS[`${prefix}_clinician`];
}
